import { ResolvedWalletCard, WalletCardPreviewProps } from './types'

/**
 * The tenant-neutral fallbacks a program falls back to until someone
 * configures it — mirroring core `WalletCardConfig` (`src/Services/Pass`) and
 * MemberCardCallout.tsx. Deliberately generic: this back-office serves several
 * clients, so a default must never name one of them (see WalletCardConfig's
 * class docblock). The member figures are sample data for the preview only.
 */
export const WALLET_DEFAULTS = {
  brandLine: 'Membership',
  cardTitle: 'Membership',
  // Final fallback only — the background prefers the program's primary color
  // first (see resolveWalletCard), matching core WalletCardConfig.
  backgroundColor: '#111111',
  foregroundColor: '#FFFFFF',
  labelColor: '#FFFFFF',
  member: {
    name: 'Alex Morgan',
    cardNumber: '0000 0000 0000',
    // ApplePassBuilder writes $renewalDate->format('Y-m-d') — keep the sample
    // in that exact shape so the preview doesn't show a format Wallet never
    // renders.
    expiryDate: '2099-12-31',
  },
  programName: 'Membership program',
} as const

/** Which top-level groups fell back to a default, for the "not configured" banner. */
export interface WalletCardDefaultsUsed {
  brandLine: boolean
  cardTitle: boolean
  colors: boolean
  member: boolean
}

export const resolveWalletCard = (
  props: WalletCardPreviewProps
): { resolved: ResolvedWalletCard; defaultsUsed: WalletCardDefaultsUsed } => {
  const colorsAreDefault =
    !props.backgroundColor && !props.foregroundColor && !props.labelColor

  const resolved: ResolvedWalletCard = {
    brandLine: props.brandLine || WALLET_DEFAULTS.brandLine,
    cardTitle: props.cardTitle || WALLET_DEFAULTS.cardTitle,
    backgroundColor:
      props.backgroundColor ||
      props.primaryColor ||
      WALLET_DEFAULTS.backgroundColor,
    foregroundColor: props.foregroundColor || WALLET_DEFAULTS.foregroundColor,
    labelColor: props.labelColor || WALLET_DEFAULTS.labelColor,
    showExpiry: props.showExpiry ?? false,
    terms: props.terms,
    supportEmail: props.supportEmail,
    supportPhone: props.supportPhone,
    supportUrl: props.supportUrl,
    member: {
      name: props.member?.name || WALLET_DEFAULTS.member.name,
      cardNumber: props.member?.cardNumber || WALLET_DEFAULTS.member.cardNumber,
      expiryDate: props.member?.expiryDate || WALLET_DEFAULTS.member.expiryDate,
    },
    programName: props.programName || WALLET_DEFAULTS.programName,
  }

  const defaultsUsed: WalletCardDefaultsUsed = {
    brandLine: !props.brandLine,
    cardTitle: !props.cardTitle,
    colors: colorsAreDefault,
    member: !props.member?.name && !props.member?.cardNumber,
  }

  return { resolved, defaultsUsed }
}
